<template>
  <div class="video-detail-wrapper">
    <div class="detail-container" v-if="video">
      <!-- 모바일 전용 헤더 -->
      <div class="detail-header">
        <button class="back-arrow-btn" @click="goBack" title="목록으로">
          ←
        </button>
        <button class="share-btn" @click="showShareModal = true" title="공유하기">
          <img src="@/assets/images/ios_share.png" alt="공유" class="share-icon" />
        </button>
      </div>

      <div class="detail-content">
        <!-- 비디오 플레이어 (클릭 전 썸네일, 클릭 후 재생) -->
        <div class="video-player-container">
          <div class="poster-blur-background" :style="posterBackgroundStyle"></div>
          <div class="video-embed-wrapper">
            <div v-if="!videoLoaded" class="thumbnail-container" @click="loadVideo">
              <img :src="`https://img.youtube.com/vi/${video.id}/sddefault.jpg`" alt="Video thumbnail"
                class="video-thumbnail" loading="lazy" />
              <div class="play-button">▶</div>
            </div>
            <iframe v-else :src="`https://www.youtube-nocookie.com/embed/${video.id}?autoplay=1&mute=0`" frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen loading="lazy"></iframe>
          </div>
          <button class="likeBtnOverlay" @click.stop="handleLikeClick" :aria-pressed="!!video.liked">
            <svg class="like-heart" :class="{ liked: video.liked }" viewBox="0 0 24 24" aria-hidden="true"
              focusable="false">
              <path fill="currentColor"
                d="M12.1 21.35l-1.1-1.02C5.14 15.24 2 12.39 2 8.99 2 6.24 4.24 4 6.99 4c1.54 0 3.04.71 4.01 1.83C11.97 4.71 13.47 4 15.01 4 17.76 4 20 6.24 20 8.99c0 3.4-3.14 6.25-8.9 11.34l-1 1.02z" />
            </svg>
          </button>
        </div>

        <!-- 상세 정보 -->
        <div class="detail-info">
          <div class="detail-title">
            <div class="title-left">
              <h1>{{ video.title }}</h1>
              <a v-if="youtubeLink" :href="youtubeLink" target="_blank" rel="noopener noreferrer"
                class="detail-more-link place-with-map" @click="trackYoutubeClick">
                YouTube에서 보기
              </a>
            </div>
            <div v-if="hasManagePermission" class="manage-btn-wrapper" @focusout="onManageWrapperFocusout">
              <button class="manage-settings-btn" @click="showManageDropdown = !showManageDropdown" title="관리 메뉴">
                <img src="@/assets/images/settings.png" alt="관리" class="settings-icon" />
              </button>
              <div v-if="showManageDropdown" class="manage-dropdown">
                <button class="manage-dropdown-item" @click="editVideo(); showManageDropdown = false">
                  수정하기
                </button>
                <button class="manage-dropdown-item manage-dropdown-item--danger"
                  @click="deleteVideo(); showManageDropdown = false" :disabled="deleteLoading">
                  {{ deleteLoading ? "삭제중..." : "삭제" }}
                </button>
              </div>
            </div>
          </div>

          <!-- 채널/작성자 정보 -->
          <div class="video-author-section">
            <div class="video-author">
              <img v-if="video.channelThumbnail" :src="video.channelThumbnail" class="author-avatar-img" alt="채널 이미지" />
              <div v-else class="author-avatar">
                {{ video.channel ? video.channel.charAt(0).toUpperCase() : "?" }}
              </div>
              <div class="author-info">
                <span class="author-name">{{ video.channel }}</span>
                <span v-if="video.channelHandle || video.channel" class="author-handle">
                  {{ video.channelHandle || `@${video.channel}` }}
                </span>
              </div>
            </div>
            <a v-if="channelUrl" :href="channelUrl" target="_blank" rel="noopener noreferrer" class="channel-open-btn"
              title="YouTube 채널로 이동">
              <span class="channel-open-icon" aria-hidden="true"></span>
            </a>
          </div>

          <div class="info-table">
            <div class="info-row" v-if="video.type">
              <div class="info-label">타입</div>
              <div class="info-value type-with-icon">
                <img v-if="videoTypeIcon" :src="videoTypeIcon" :alt="videoTypeLabel" class="type-icon" />
                <span>{{ videoTypeLabel }}</span>
              </div>
            </div>
            <div class="info-row" v-if="video.isCover === true || video.isCover === false">
              <div class="info-label">분류</div>
              <div class="info-value">{{ video.isCover ? '커버' : '오리지널' }}</div>
            </div>
            <div class="info-row" v-if="video.date">
              <div class="info-label">업로드일</div>
              <div class="info-value">{{ formatVideoDate(video.date) }}</div>
            </div>
            <div class="info-row" v-if="lineupDisplayText">
              <div class="info-label">라인업</div>
              <div class="info-value">{{ lineupDisplayText }}</div>
            </div>
            <div class="info-row" v-if="crewDisplayText">
              <div class="info-label">함께한 사람들</div>
              <div class="info-value crew-display">{{ crewDisplayText }}</div>
            </div>
            <!-- 원곡: 카드(originalSongInfo) 또는 링크만 표시 -->
            <div class="info-row info-row--full" v-if="video.isCover && (video.originalSongInfo || video.originalSong)">
              <div class="info-label">원곡</div>
              <div class="info-value">
                <a v-if="video.originalSongInfo" :href="originalSongUrl" target="_blank" rel="noopener noreferrer"
                  class="original-song-card">
                  <span class="original-song-card__thumb">
                    <img v-if="video.originalSongInfo.videoThumbnail" :src="video.originalSongInfo.videoThumbnail"
                      :alt="video.originalSongInfo.title" />
                    <span v-else class="original-song-card__placeholder">▶</span>
                  </span>
                  <span class="original-song-card__body">
                    <span class="original-song-card__title">{{ video.originalSongInfo.title }}</span>
                    <span class="original-song-card__channel">{{ video.originalSongInfo.channel }}</span>
                    <span class="original-song-card__link">YouTube에서 보기</span>
                  </span>
                </a>
                <template v-else>
                  <a v-if="isYoutubeUrl(video.originalSong)" :href="video.originalSong" target="_blank"
                    rel="noopener noreferrer" class="original-song-link">YouTube에서 보기</a>
                  <span v-else>{{ video.originalSong }}</span>
                </template>
              </div>
            </div>
            <div class="info-row" v-if="video.liveId">
              <div class="info-label">라이브</div>
              <div class="info-value info-value-with-action">
                <span>{{ video.liveTitle || "라이브 등록 영상" }}</span>
                <button class="detail-link-button" @click="goToLiveDetail(video.liveId)">바로가기</button>
              </div>
            </div>
            <div class="info-row" v-else-if="video.registeredByName">
              <div class="info-label">등록자</div>
              <div class="info-value">{{ video.registeredByName }}</div>
            </div>

          </div>


          <!-- 설명 -->
          <div v-if="video.description" class="video-description">
            <h3 class="description-label">설명</h3>
            <p class="description-text">{{ video.description }}</p>
          </div>

          <!-- 라인업 / 함께한 사람들 (아티스트 카드) -->
          <div v-if="lineupArtists.length > 0 || crewArtists.length > 0" class="artist-sections-wrapper">
            <div v-if="lineupArtists.length > 0" class="artist-section">
              <span class="artist-section-label">라인업</span>
              <div class="lineup-grid">
                <ArtistCard v-for="(artistObj, index) in lineupArtists" :key="`lineup-${index}`" :artist="artistObj"
                  variant="soft" compact :is-liked="isArtistLiked(artistObj.id)" :show-like-button="true"
                  @like-toggle="handleArtistLikeToggle" />
              </div>
            </div>
            <div v-if="crewArtists.length > 0" class="artist-section">
              <span class="artist-section-label">함께한 사람들</span>
              <div class="lineup-grid">
                <ArtistCard v-for="(artistObj, index) in crewArtists" :key="`crew-${index}`" :artist="artistObj"
                  variant="soft" compact :role-badge="artistObj.role || ''" :is-liked="isArtistLiked(artistObj.id)"
                  :show-like-button="true" @like-toggle="handleArtistLikeToggle" />
              </div>
            </div>
          </div>

          <!-- 관리자 전용 섹션 -->
          <div v-if="userStore.auth === 'admin'" class="admin-section">
            <div class="admin-section-header" @click="adminSectionOpen = !adminSectionOpen">
              <span class="admin-section-title">🔧 관리자 메뉴</span>
              <span class="admin-section-toggle">{{ adminSectionOpen ? "▲" : "▼" }}</span>
            </div>
            <div v-show="adminSectionOpen" class="admin-section-content">
              <div class="admin-info-table">
                <div class="admin-info-row" v-if="video.registeredBy">
                  <div class="admin-info-label">등록자 이메일</div>
                  <div class="admin-info-value">{{ video.registeredBy }}</div>
                </div>
                <div class="admin-info-row">
                  <div class="admin-info-label">찜 정보</div>
                  <div class="admin-info-value">
                    <span class="like-count-badge">❤️ {{ getTotalLikes(video) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 광고 섹션 -->
      <div v-if="adBanners.length > 0" class="ad-section-wrapper">
        <AdSection :adBanners="adBanners" @ad-click="goToAdLink" />
      </div>

    </div>


    <div v-else-if="loading" class="loading">
      <div class="loading-container">
        <div class="spinner"></div>
        <p class="loading-text">영상 정보를 불러오는 중...</p>
      </div>
    </div>

    <div v-else-if="notFound" class="not-found">
      <p>해당 영상을 찾을 수 없습니다.</p>
      <button class="back-to-list-btn" @click="goBack">목록으로 돌아가기</button>
    </div>

    <!-- 수정 팝업 -->
    <video-form v-if="showVideoForm" :is-visible="showVideoForm" :edit-mode="true" :edit-data="video"
      @close-popup="closeVideoForm" @update-success="onUpdateSuccess" />

    <!-- 공유 방법 선택 모달 -->
    <share-options-modal :visible="showShareModal" @close="showShareModal = false"
      @select="handleShareMethod"></share-options-modal>
  </div>
</template>

<script>
import { doc, getDoc, deleteDoc, collection, getDocs, query, where } from "firebase/firestore";
import { ref as storageRef } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import VideoForm from "@/components/video/VideoForm.vue";
import ArtistCard from "@/components/ArtistCard.vue";
import AdSection from "@/components/AdSection.vue";
import scrollManager from "@/utils/scrollManager";
import { trackActivity, ACTIVITY_TYPES, trackShare } from "@/utils/activityTracker";
import { incrementBannerClickCount } from "@/utils/write/banner";
import { setPageMeta, shareViaKakao, shareViaTwitter } from "@/utils/pageMeta";
import ShareOptionsModal from "@/components/modal/ShareOptionsModal.vue";
import { createNotification } from "@/utils/write/alarm";
import { incrementVideoViewCount } from "@/utils/write/video";

const VIDEO_TYPE_LABELS = {
  solo: "솔로",
  duet: "듀엣",
  team: "팀",
  virtual: "버츄얼",
  band: "밴드",
};

const VIDEO_TYPE_TO_ICON = {
  solo: "vocal",
  duet: "mix",
  team: "mix",
  virtual: "mix",
  band: "band",
};

export default {
  name: "VideoDetail",
  components: {
    VideoForm,
    ArtistCard,
    AdSection,
    ShareOptionsModal,
  },
  data() {
    return {
      video: null,
      loading: true,
      notFound: false,
      deleteLoading: false,
      showVideoForm: false,
      adminSectionOpen: false,
      adBanners: [],
      videoLoaded: false,
      userStore: useUserStore(),
      artists: [], // 라인업/함께한 사람들 아티스트 정보 (Firestore 조회)
      userArtistLikes: [], // 로그인 사용자 찜한 아티스트 ID 목록
      showShareModal: false,
      showManageDropdown: false,
    };
  },
  computed: {
    youtubeLink() {
      if (!this.video?.id) return null;
      return `https://www.youtube.com/watch?v=${this.video.id}`;
    },
    channelUrl() {
      if (!this.video?.channelId) return null;
      return `https://www.youtube.com/channel/${this.video.channelId}`;
    },
    originalSongUrl() {
      if (this.video?.originalSongInfo?.id) {
        return `https://www.youtube.com/watch?v=${this.video.originalSongInfo.id}`;
      }
      return this.video?.originalSong || null;
    },
    posterBackgroundStyle() {
      if (!this.video?.id) return "";
      const thumb = `https://img.youtube.com/vi/${this.video.id}/sddefault.jpg`;
      return `background-image: url(${thumb})`;
    },
    videoTypeLabel() {
      if (!this.video?.type) return "";
      const type = this.video.type.toLowerCase();
      return VIDEO_TYPE_LABELS[type] || this.video.type;
    },
    videoTypeIcon() {
      if (!this.video?.type) return null;
      const type = this.video.type.toLowerCase();
      const iconKey = VIDEO_TYPE_TO_ICON[type] || "mix";
      try {
        return require(`@/assets/images/${iconKey}.png`);
      } catch {
        return require("@/assets/images/mix.png");
      }
    },
    lineupDisplayText() {
      const lineup = this.video?.lineup;
      if (!lineup) return "";
      const arr = Array.isArray(lineup) ? lineup : [lineup];
      return arr.filter(Boolean).join(", ");
    },
    crewDisplayText() {
      const crew = this.video?.collaboratorDetails || this.video?.collaborators;
      if (!crew) return "";
      const arr = Array.isArray(crew) ? crew : [];
      return arr
        .map((c) => {
          const name = c.name || c.userName || c.userNickname;
          const role = c.role;
          return role ? `${name} (${role})` : name;
        })
        .filter(Boolean)
        .join(", ");
    },
    hasManagePermission() {
      if (!this.video || !this.userStore.isLogin) return false;
      if (this.userStore.auth === "admin") return true;
      return this.video.registeredBy === this.userStore.email;
    },
    lineupArtists() {
      const names = this.extractLineupNames();
      if (names.length === 0) return [];
      return names.map((name) => {
        const info = this.artists.find((a) => a.name === name);
        return {
          id: info?.id ?? null,
          slug: info?.slug ?? null,
          name,
          type: info?.type ?? null,
          detailLink: info?.detailLink ?? null,
          profileImage: info?.profileImage ?? null,
        };
      });
    },
    crewArtists() {
      const members = this.extractCrewMembers();
      if (members.length === 0) return [];
      return members.map((member) => {
        const info = this.artists.find((a) => a.name === member.name);
        return {
          id: info?.id ?? null,
          slug: info?.slug ?? null,
          name: member.name,
          role: member.role || "",
          type: info?.type ?? null,
          detailLink: info?.detailLink ?? null,
          profileImage: info?.profileImage ?? null,
        };
      });
    },
  },
  async mounted() {
    await this.fetchVideoDetail();
  },
  watch: {
    "$route.params.id": {
      async handler(newId) {
        if (newId) {
          this.video = null;
          this.videoLoaded = false;
          this.loading = true;
          this.notFound = false;
          await this.fetchVideoDetail();
        }
      },
    },
  },
  methods: {
    goBack() {
      this.$router.push("/video");
    },
    goToLiveDetail(liveId) {
      if (!liveId) return;
      this.$router.push(`/live/${liveId}`);
    },
    loadVideo() {
      this.videoLoaded = true;
      if (this.video?.id) {
        trackActivity(ACTIVITY_TYPES.VIDEO_PLAY, {
          target: "video",
          targetId: this.video.id,
        }).catch(() => { });
        incrementVideoViewCount(this.video.id).catch(() => { });
        this.video = {
          ...this.video,
          viewCount: (this.video.viewCount ?? 0) + 1,
        };
      }
    },
    async fetchVideoDetail() {
      try {
        this.loading = true;
        this.notFound = false;
        const videoId = this.$route.params.id;
        if (!videoId) {
          this.notFound = true;
          this.loading = false;
          return;
        }

        const videoDoc = await getDoc(doc(firestore, "videos", videoId));

        if (videoDoc.exists()) {
          const data = videoDoc.data();
          this.video = {
            id: videoDoc.id,
            ...data,
            liked: false,
          };
          const thumbUrl = `https://img.youtube.com/vi/${this.video.id}/sddefault.jpg`;
          setPageMeta({
            title: this.video.title,
            description: this.video.channel || this.video.title,
            image: thumbUrl,
          });
          // 찜 상태를 먼저 조회해 좋아요 표시를 빨리 함
          await this.fetchLikedStatus();
          // 화면은 여기서 표시하고, 광고/아티스트는 백그라운드 로드
          this.loading = false;
          Promise.all([
            this.fetchAdBanners(),
            this.fetchArtists(),
            this.loadUserArtistLikes(),
          ]).catch((err) => console.warn("비디오 상세 보조 데이터 로드 실패:", err));
        } else {
          this.notFound = true;
        }
      } catch (error) {
        console.error("영상 조회 실패:", error);
        this.notFound = true;
      } finally {
        this.loading = false;
      }
    },
    async fetchLikedStatus() {
      if (!this.userStore.isLogin || !this.video) return;
      try {
        const { getUserLikes } = await import("@/utils/read/user");
        const likes = await getUserLikes(this.userStore.email);
        const videoLikes = likes.videoLikes || likes.likes || [];
        if (this.video) {
          this.video = { ...this.video, liked: videoLikes.includes(this.video.id) };
        }
      } catch (error) {
        console.error("찜 상태 조회 실패:", error);
      }
    },
    getTotalLikes(v) {
      return (v?.likes || 0) + (v?.tweetLikes || 0) + (v?.youtubeLikes || 0);
    },
    formatVideoDate(dateStr) {
      if (!dateStr) return "";
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      } catch {
        return String(dateStr).substring(0, 10);
      }
    },
    isYoutubeUrl(url) {
      if (!url || typeof url !== "string") return false;
      return url.includes("youtube.com/watch?v=") || url.includes("youtu.be/");
    },
    extractLineupNames() {
      const lineup = this.video?.lineup;
      if (!lineup) return [];
      const arr = Array.isArray(lineup) ? lineup : [lineup];
      return arr.map((item) => (typeof item === "object" && item != null ? item.name : item)).map((n) => String(n || "").trim()).filter(Boolean);
    },
    extractCrewMembers() {
      const crew = this.video?.collaboratorDetails || this.video?.collaborators;
      if (!crew || !Array.isArray(crew)) return [];
      return crew
        .map((c) => {
          const name = (c.name ?? c.userName ?? c.userNickname ?? "").trim();
          const role = (c.role ?? "").trim();
          return { name, role };
        })
        .filter((m) => m.name);
    },
    async fetchArtists() {
      const lineupNames = this.extractLineupNames();
      const crewNames = this.extractCrewMembers().map((m) => m.name);
      const targetNames = [...new Set([...lineupNames, ...crewNames])];
      if (targetNames.length === 0) {
        this.artists = [];
        return;
      }
      try {
        this.artists = await Promise.all(
          targetNames.map(async (artistName) => {
            try {
              const q = query(collection(firestore, "artists"), where("name", "==", artistName));
              const snap = await getDocs(q);
              if (snap.empty) {
                return { id: null, slug: null, name: artistName, type: null, detailLink: null, profileImage: null };
              }
              const docSnap = snap.docs[0];
              const artistData = docSnap.data();
              if (!artistData.approved && this.userStore.auth !== "admin") {
                return { id: null, slug: null, name: artistName, type: null, detailLink: null, profileImage: null };
              }
              let profileImageUrl = null;
              if (artistData.userProfileImg) {
                try {
                  const imageRef = storageRef(storage, `profile/${artistData.userProfileImg}`);
                  profileImageUrl = await getDownloadURL(imageRef);
                } catch {
                  profileImageUrl = null;
                }
              } else if (artistData.img) {
                try {
                  const imagePath = artistData.img.includes("/") ? artistData.img : `artist/${artistData.img}`;
                  const imageRef = storageRef(storage, imagePath);
                  profileImageUrl = await getDownloadURL(imageRef);
                } catch {
                  profileImageUrl = null;
                }
              }
              return {
                id: docSnap.id,
                slug: artistData.slug ?? null,
                name: artistData.name ?? artistName,
                type: artistData.type ?? null,
                detailLink: artistData.detailLink ?? null,
                profileImage: profileImageUrl,
              };
            } catch (err) {
              console.warn(`아티스트 ${artistName} 로드 실패:`, err);
              return { id: null, slug: null, name: artistName, type: null, detailLink: null, profileImage: null };
            }
          })
        );
      } catch (err) {
        console.error("아티스트 정보 로드 실패:", err);
        this.artists = [];
      }
    },
    async loadUserArtistLikes() {
      if (!this.userStore.isLogin) {
        this.userArtistLikes = [];
        return;
      }
      try {
        const { getUserLikes } = await import("@/utils/read/user");
        const likes = await getUserLikes(this.userStore.email);
        this.userArtistLikes = likes.artistLikes || [];
      } catch (err) {
        console.warn("아티스트 찜 목록 로드 실패:", err);
        this.userArtistLikes = [];
      }
    },
    isArtistLiked(artistId) {
      return artistId != null && this.userArtistLikes.includes(artistId);
    },
    async handleArtistLikeToggle(artistId) {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup("artistLike");
        return;
      }
      if (artistId == null) return;
      try {
        const { toggleArtistLike } = await import("@/utils/write/user");
        const isCurrentlyLiked = this.isArtistLiked(artistId);
        await toggleArtistLike(artistId, this.userStore.email, isCurrentlyLiked);
        if (isCurrentlyLiked) {
          this.userArtistLikes = this.userArtistLikes.filter((id) => id !== artistId);
        } else {
          this.userArtistLikes = [...this.userArtistLikes, artistId];
        }
      } catch (err) {
        console.error("아티스트 찜 처리 실패:", err);
      }
    },
    handleLikeClick() {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup("videoLike");
        return;
      }
      if (!this.userStore.email) {
        this.userStore.showLoginPopup("videoLike");
        return;
      }
      this.toggleLike();
    },
    async toggleLike() {
      if (!this.video || !this.userStore.email) return;
      try {
        const { toggleVideoLike } = await import("@/utils/write/user");
        const isLiked = await toggleVideoLike(this.video.id, this.userStore.email);

        this.video = {
          ...this.video,
          liked: isLiked,
          likes: isLiked
            ? (this.video.likes || 0) + 1
            : Math.max(0, (this.video.likes || 0) - 1),
        };

        if (isLiked) {
          const ownerEmail = this.video.registeredBy;
          if (ownerEmail && ownerEmail !== this.userStore.email) {
            try {
              const videoTitle = this.video.title?.length > 25
                ? this.video.title.substring(0, 25) + "..."
                : (this.video.title || "영상");
              await createNotification({
                userId: ownerEmail,
                type: "VIDEO_LIKE",
                title: "영상에 찜을 받았어요! 💖",
                message: `${this.userStore.nickname || "누군가"}님이 "${videoTitle}" 영상을 찜했습니다.`,
                targetType: "video",
                targetId: this.video.id,
                fromUserId: this.userStore.email,
                fromUserNickname: this.userStore.nickname || null,
              });
            } catch (notifError) {
              console.warn("영상 찜 알림 전송 실패:", notifError);
            }
          }
        }
      } catch (error) {
        console.error("좋아요 처리 중 오류:", error);
        if (this.$alert) this.$alert("찜 처리에 실패했습니다. 다시 시도해 주세요.");
      }
    },
    async handleShareMethod(method) {
      this.showShareModal = false;
      const url = window.location.href;
      const title = `${this.video?.title || "영상"} - 링꾹라이브`;

      if (method === "kakao") {
        await shareViaKakao();
        trackShare("video", this.video?.id, "kakao");
        return;
      }
      if (method === "twitter") {
        shareViaTwitter();
        trackShare("video", this.video?.id, "twitter");
        return;
      }
      if (method === "copy") {
        trackShare("video", this.video?.id, "copy");
        this.copyToClipboard(url);
        return;
      }
      if (method === "native" && navigator.share) {
        try {
          await navigator.share({ title, url });
          trackShare("video", this.video?.id, "native");
        } catch (error) {
          if (error.name !== "AbortError") {
            trackShare("video", this.video?.id, "copy_fallback");
            this.copyToClipboard(url);
          }
        }
      }
    },
    copyToClipboard(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          (this.$alert || window.$alert)?.("링크가 복사되었습니다.");
        })
        .catch(() => {
          (this.$alert || window.$alert)?.("링크 복사에 실패했습니다.");
        });
    },
    trackYoutubeClick() {
      trackActivity(ACTIVITY_TYPES.DETAIL_LINK_CLICK, {
        target: "video",
        targetId: this.video?.id,
        metadata: { link: this.youtubeLink },
      });
    },
    async fetchAdBanners() {
      try {
        const bannersQuery = query(
          collection(firestore, "banners"),
          where("active", "==", true),
          where("type", "==", "detail")
        );
        const bannersSnapshot = await getDocs(bannersQuery);
        const customBannerData = await Promise.all(
          bannersSnapshot.docs.map(async (docSnap) => {
            const data = docSnap.data();
            if (data.startDateTime || data.endDateTime) {
              const now = new Date();
              if (data.startDateTime && new Date(data.startDateTime) > now) return null;
              if (data.endDateTime && new Date(data.endDateTime) < now) return null;
            }
            let bannerImgUrl = data.img;
            if (!bannerImgUrl) return null;
            if (!bannerImgUrl.startsWith("http")) {
              try {
                const imageRef = storageRef(storage, `banner/${data.img}`);
                bannerImgUrl = await getDownloadURL(imageRef);
              } catch {
                return null;
              }
            }
            return {
              id: docSnap.id,
              source: "custom",
              title: data.title,
              img: bannerImgUrl,
              link: data.link || "",
              priority: data.priority || 0,
            };
          })
        );

        const allBanners = customBannerData.filter(Boolean);
        allBanners.sort((a, b) => (b.priority || 0) - (a.priority || 0));
        this.adBanners = allBanners;
      } catch (error) {
        console.error("광고 배너 로드 실패:", error);
        this.adBanners = [];
      }
    },
    async goToAdLink(ad) {
      await this.incrementAdClickCount(ad);
      if (ad.source === "custom" && ad.link) {
        window.open(ad.link, "_blank");
      } else if (ad.source === "live") {
        this.$router.push({ name: "LiveDetail", params: { slug: ad.slug || ad.id } });
      }
    },
    async incrementAdClickCount(ad) {
      try {
        await incrementBannerClickCount(ad);
      } catch (error) {
        console.error("광고 클릭 횟수 증가 오류:", error);
      }
    },
    onManageWrapperFocusout(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        this.showManageDropdown = false;
      }
    },

    editVideo() {
      this.showVideoForm = true;
      scrollManager.lock("videoDetailForm");
    },
    closeVideoForm() {
      this.showVideoForm = false;
      scrollManager.unlock("videoDetailForm");
    },
    onUpdateSuccess() {
      this.closeVideoForm();
      this.fetchVideoDetail();
    },
    async deleteVideo() {
      if (!this.video) return;
      const confirmed = await this.$confirm(`"${this.video.title}" 영상을 삭제하시겠습니까?`);
      if (!confirmed) return;

      try {
        this.deleteLoading = true;
        await deleteDoc(doc(firestore, "videos", this.video.id));
        (this.$alert || window.$alert)("삭제되었습니다.");
        this.$router.push("/video");
      } catch (error) {
        console.error("삭제 실패:", error);
        (this.$alert || window.$alert)("삭제에 실패했습니다.");
      } finally {
        this.deleteLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.video-detail-wrapper {
  min-height: 100vh;
  width: 100%;
  background: linear-gradient(180deg, #fdf8f5 0%, #f5eef8 35%, #f0f4ff 100%);
  padding-bottom: 24px;
}

.detail-container {
  max-width: 900px;
  width: 90%;
  margin: 0 auto;
  padding: 0 16px;
  display: flex;
  flex-direction: column;
  min-width: 0;
  box-sizing: border-box;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
  margin-bottom: 0;
}

/* 모바일: 헤더 표시, 데스크톱 공유 버튼 숨김 */
.mobile-only {
  display: flex;
}

.desktop-only {
  display: none !important;
}

@media (min-width: 769px) {
  .detail-header.mobile-only {
    display: none;
  }

  .desktop-only {
    display: flex !important;
  }
}

.back-arrow-btn {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 6px 10px;
  color: #2d1b4e;
  transition: opacity 0.2s ease;
}

.back-arrow-btn:hover {
  opacity: 0.7;
}

.share-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.share-btn:hover {
  opacity: 0.7;
}

.share-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.manage-btn-wrapper {
  position: relative;
  flex-shrink: 0;
}

.manage-settings-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
  border-radius: 6px;
}

.manage-settings-btn:hover {
  opacity: 0.7;
}

.settings-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  opacity: 0.45;
}

.manage-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  min-width: 120px;
  z-index: 100;
  overflow: hidden;
}

.manage-dropdown-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;
  transition: background 0.15s;
}

.manage-dropdown-item:hover {
  background: #f5f5f5;
}

.manage-dropdown-item--danger {
  color: #e53935;
}

.manage-dropdown-item--danger:hover {
  background: #fff5f5;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  width: 100%;
}

.video-player-container {
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: #000;
  box-shadow: 0 8px 32px rgba(45, 27, 78, 0.12), 0 2px 8px rgba(0, 0, 0, 0.06);
}

.poster-blur-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  filter: blur(20px);
  opacity: 0.4;
  z-index: 0;
}

.video-embed-wrapper {
  position: relative;
  z-index: 1;
  width: 100%;
  padding-bottom: 56.25%;
  height: 0;
}

.video-embed-wrapper iframe {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.thumbnail-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #000;
}

.video-thumbnail {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.2s;
}

.thumbnail-container:hover .video-thumbnail {
  opacity: 0.7;
}

.play-button {
  position: absolute;
  color: white;
  font-size: 48px;
  opacity: 0.8;
  transition: opacity 0.2s, transform 0.2s;
}

.thumbnail-container:hover .play-button {
  opacity: 1;
  transform: scale(1.2);
}

.likeBtnOverlay {
  position: absolute;
  bottom: 12px;
  right: 12px;
  z-index: 2;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.likeBtnOverlay:hover {
  background: rgba(0, 0, 0, 0.7);
}

.like-heart {
  width: 24px;
  height: 24px;
  color: #bdbdbd;
  transition: color 0.2s ease;
}

.like-heart.liked {
  color: #ff6b9d;
}

.detail-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
  width: 100%;
}

.detail-title {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
}

.title-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.detail-title h1 {
  font-size: 1.4rem;
  color: #2d1b4e;
  margin: 0;
  word-break: keep-all;
  font-family: "Paperlogy8", "NanumSquareRoundEB", sans-serif;
  letter-spacing: -0.02em;
  line-height: 1.5;
  font-weight: 600;
}

.detail-more-link {
  font-size: 13px;
  color: #8b6bb8;
  text-decoration: none;
  font-family: "NanumSquareRoundR", sans-serif;
  cursor: pointer;
  white-space: nowrap;
  opacity: 0.9;
  transition: opacity 0.2s ease;
}

.detail-more-link:hover {
  opacity: 1;
  text-decoration: underline;
}

.original-song-link {
  font-size: 14px;
  color: #8b6bb8;
  text-decoration: none;
}

.original-song-link:hover {
  text-decoration: underline;
}

.original-song-card {
  display: flex;
  align-items: stretch;
  gap: 12px;
  width: 100%;
  padding: 0;
  border-radius: 14px;
  overflow: hidden;
  /* border: 1px solid rgba(139, 107, 184, 0.2); */
  background: rgba(255, 255, 255, 0.8);
  /* box-shadow: 0 2px 12px rgba(45, 27, 78, 0.06); */
  text-decoration: none;
  color: inherit;
  transition: all 0.25s ease;
}

.original-song-card:hover {
  box-shadow: 0 6px 20px rgba(45, 27, 78, 0.12);
  transform: translateY(-1px);
}

.original-song-card__thumb {
  width: 160px;
  min-width: 160px;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.original-song-card__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.original-song-card__placeholder {
  color: rgba(255, 255, 255, 0.7);
  font-size: 24px;
}

.original-song-card__body {
  flex: 1;
  min-width: 0;
  padding: 10px 12px 10px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
}

.original-song-card__title {
  font-size: 14px;
  font-weight: 600;
  color: #2d1b4e;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.original-song-card__channel {
  font-size: 12px;
  color: #7b6b94;
}

.original-song-card__link {
  font-size: 12px;
  color: #8b6bb8;
  margin-top: 2px;
}

.original-song-card:hover .original-song-card__link {
  text-decoration: underline;
}

.place-with-map {
  white-space: nowrap;
}

.type-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  border: 1px solid rgba(139, 107, 184, 0.25);
  border-radius: 50%;
  padding: 2px;
}

.crew-display {
  line-height: 1.5;
  align-items: flex-start;
  word-break: break-word;
}

.video-author-section {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  background: rgba(255, 255, 255, 0.6);
  border-radius: 16px;
  backdrop-filter: blur(8px);
}

.video-author {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.author-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #b8a9d4 0%, #e8b4d4 50%, #a8d4f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.25rem;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(139, 107, 184, 0.25);
}

.author-avatar-img {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.author-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.author-name {
  font-family: "NanumSquareRoundB", sans-serif;
  font-weight: bold;
  font-size: 1rem;
  color: #2d1b4e;
}

.author-handle {
  font-size: 0.875rem;
  color: #7b6b94;
}

.channel-open-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  transition: opacity 0.2s ease, background 0.2s ease;
}

.channel-open-btn:hover {
  opacity: 0.8;
  background: rgba(139, 107, 184, 0.1);
}

.channel-open-icon {
  display: inline-block;
  width: 22px;
  height: 22px;
  background-color: #7b6b94;
  -webkit-mask: url(~@/assets/images/open_in_new_200.png) no-repeat center / contain;
  mask: url(~@/assets/images/open_in_new_200.png) no-repeat center / contain;
}

.like-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 24px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.like-button:hover {
  background: rgba(255, 107, 157, 0.1);
}

.like-button.liked .like-icon {
  filter: none;
}

.like-icon {
  width: 20px;
  height: 20px;
}

.like-count {
  font-size: 0.875rem;
  color: #7b6b94;
  font-family: "NanumSquareRoundR", sans-serif;
}

.info-table {
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
  width: 100%;
}

.info-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 12px;
  transition: background 0.2s ease;
}

.info-row:hover {
  background: rgba(255, 255, 255, 0.75);
}

.info-row--full {
  flex-direction: column;
  gap: 8px;
}

.info-row--full .info-value {
  width: 100%;
}

.info-label {
  width: 75px;
  min-width: 75px;
  color: #8b7ba3;
  font-size: 12px;
  font-weight: 500;
  font-family: "NanumSquareRoundR", sans-serif;
  letter-spacing: 0.02em;
  align-self: center;
}

.info-value {
  color: #3d2d5c;
  font-size: 14px;
  font-family: "NanumSquareRoundR", sans-serif;
  font-weight: 400;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  word-break: break-word;
}

.info-value-with-action {
  justify-content: space-between;
  width: 100%;
}

.detail-link-button {
  padding: 6px 10px;
  border: 1px solid rgba(124, 58, 237, 0.18);
  border-radius: 999px;
  background: rgba(124, 58, 237, 0.08);
  color: #6d28d9;
  font-size: 12px;
  font-family: "NanumSquareRoundB", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.detail-link-button:hover {
  background: rgba(124, 58, 237, 0.14);
  border-color: rgba(124, 58, 237, 0.28);
}

.info-value p {
  margin: 0;
}

.edit-btn,
.delete-btn {
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.edit-btn:focus,
.delete-btn:focus {
  outline: none;
}

.edit-btn {
  background: linear-gradient(135deg, #8b6bb8 0%, #a88bc4 100%);
  color: white;
  border: none;
}

.delete-btn {
  background: #f44336;
  color: white;
  border: none;
}

.edit-btn:hover,
.delete-btn:hover:not(:disabled) {
  opacity: 0.9;
}

.delete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 관리자 전용 섹션 스타일 */
.admin-section {
  width: 100%;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(139, 107, 184, 0.12);
  box-shadow: 0 2px 12px rgba(45, 27, 78, 0.06);
  overflow: hidden;
}

.admin-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}

.admin-section-header:hover {
  background: rgba(139, 107, 184, 0.04);
}

.admin-section-title {
  font-size: 13px;
  font-weight: 500;
  color: #8b7ba3;
  font-family: "NanumSquareRoundR", sans-serif;
  letter-spacing: 0.02em;
}

.admin-section-toggle {
  font-size: 10px;
  color: #8b7ba3;
  transition: transform 0.3s ease;
}

.admin-section-content {
  padding: 20px 20px;
}

.admin-info-table {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
}

.admin-info-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.admin-info-label {
  width: 65px;
  min-width: 65px;
  font-size: 11px;
  font-weight: 500;
  color: #8b7ba3;
  font-family: "NanumSquareRoundR", sans-serif;
}

.admin-section .admin-info-value {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #3d2d5c;
  font-family: "NanumSquareRoundR", sans-serif;
}

.admin-sub-text {
  font-size: 11px;
  color: #8b7ba3;
}

.admin-date-text {
  font-size: 10px;
  color: #9b8bb3;
  margin-left: 4px;
}

.admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(139, 107, 184, 0.12);
}

.admin-actions .edit-btn,
.admin-actions .delete-btn {
  margin: 0;
  min-height: 28px;
  padding: 6px 12px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  white-space: nowrap;
}

.like-count-badge {
  font-size: 13px;
}

.ad-section-wrapper {
  margin-top: 8px;
  width: 100%;
  min-width: 0;
}

.video-description {
  margin-top: 8px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
}

.description-label {
  font-size: 12px;
  color: #8b7ba3;
  font-weight: 500;
  margin: 0 0 10px 0;
  letter-spacing: 0.05em;
}

.description-text {
  font-size: 14px;
  color: #3d2d5c;
  line-height: 1.75;
  white-space: pre-wrap;
  margin: 0;
}

.artist-sections-wrapper {
  padding: 20px;
  background: rgba(255, 255, 255, 0.5);
  border-radius: 16px;
  transition: background 0.2s ease;
}

.artist-sections-wrapper:hover {
  background: rgba(255, 255, 255, 0.65);
}

.artist-section {
  padding: 0;
}

.artist-section+.artist-section {
  margin-top: 20px;
  padding-top: 18px;
  border-top: 1px solid rgba(139, 107, 184, 0.1);
}

.artist-section-label {
  display: inline-block;
  font-size: 12px;
  color: #8b7ba3;
  font-weight: 500;
  margin-bottom: 12px;
  letter-spacing: 0.08em;
  font-family: "NanumSquareRoundR", sans-serif;
}

.lineup-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.loading,
.not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 16px;
  padding: 40px 20px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-text {
  font-size: 14px;
  color: #666;
  margin: 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(139, 107, 184, 0.2);
  border-top-color: #8b6bb8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading p,
.not-found p {
  font-size: 14px;
  color: #666;
}

.back-to-list-btn {
  padding: 10px 20px;
  background: linear-gradient(135deg, #8b6bb8 0%, #a88bc4 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.2s;
}

.back-to-list-btn:hover {
  opacity: 0.9;
}

.back-to-list-btn:focus {
  outline: none;
}

/* 480px 이하 반응형 */
@media screen and (max-width: 480px) {
  .video-detail-wrapper {
    padding-bottom: 16px;
  }

  .detail-container {
    padding: 0px 10px;
  }


  .share-icon {
    width: 20px;
    height: 20px;
  }

  .back-arrow-btn {
    font-size: 18px;
    padding: 4px 8px;
  }

  .video-player-container {
    border-radius: 8px;
  }

  .play-button {
    font-size: 36px;
  }

  .likeBtnOverlay {
    width: 38px;
    height: 38px;
    bottom: 8px;
    right: 8px;
  }

  .like-heart {
    width: 20px;
    height: 20px;
  }

  .detail-title h1 {
    font-size: 1.1rem;
  }

  .detail-more-link {
    font-size: 12px;
  }

  .author-avatar,
  .author-avatar-img {
    width: 40px;
    height: 40px;
    font-size: 1rem;
  }

  .author-name {
    font-size: 0.9375rem;
  }

  .author-handle {
    font-size: 0.8125rem;
  }

  .like-button {
    padding: 6px 10px;
  }

  .like-icon {
    width: 18px;
    height: 18px;
  }

  .like-count {
    font-size: 0.8125rem;
  }

  .lineup-grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .info-label {
    width: 70px;
    min-width: 70px;
    font-size: 11px;
  }

  .info-value {
    font-size: 12px;
  }

  .type-icon {
    width: 18px;
    height: 18px;
  }

  .edit-btn,
  .delete-btn {
    padding: 5px 10px;
    font-size: 11px;
  }

  .admin-section {
    border-radius: 12px;
  }

  .admin-section-header {
    padding: 12px 16px;
  }

  .admin-section-title {
    font-size: 12px;
  }

  .admin-section-content {
    padding: 16px 16px;
  }

  .admin-info-label {
    width: 55px;
    min-width: 55px;
    font-size: 10px;
  }

  .admin-section .admin-info-value {
    font-size: 12px;
  }

  .description-text {
    font-size: 13px;
  }

  .ad-section-wrapper {
    margin-top: 20px;
  }

  .loading,
  .not-found {
    min-height: 250px;
    padding: 24px 16px;
  }

  .back-to-list-btn {
    padding: 8px 16px;
    font-size: 13px;
  }
}
</style>
